import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { IoIosAdd } from 'react-icons/io';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
} from '../../util/types';
import { parse } from '../../util/urlHelpers';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  ManageProjectListingCard,
  Page,
  PaginationLinks,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { NamedLink } from '../../components';

import {
  closeListing,
  openListing,
  discardDraft,
  hideListing,
  showListing,
  getOwnListingsById,
  queryOwnListings,
} from './ManageProjectListingsPage.duck';
import css from './ManageProjectListingsPage.css';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// How long the "Draft deleted / Undo" toast waits before the draft is
// actually discarded. Keep in sync with the `undoCountdown` animation duration
// in the CSS.
export const UNDO_DISCARD_WINDOW_MS = 5000;

export class ManageProjectListingsPageComponent extends Component {
  constructor(props) {
    super(props);
    // pendingDiscard: the draft the undo toast refers to, not yet discarded.
    // hiddenListingIds: drafts kept off the page while pending or while their
    // discard request runs; a draft comes back only if undone or if the
    // discard fails.
    this.state = { pendingDiscard: null, hiddenListingIds: [] };
    this.discardTimeout = null;
    this.startDiscard = this.startDiscard.bind(this);
    this.undoDiscard = this.undoDiscard.bind(this);
    this.flushPendingDiscard = this.flushPendingDiscard.bind(this);
  }

  componentWillUnmount() {
    this.unmounted = true;
    // Leaving the page doesn't cancel a delete the user already confirmed.
    window.clearTimeout(this.discardTimeout);
    if (this.state.pendingDiscard) {
      this.commitDiscard(this.state.pendingDiscard);
    }
  }

  startDiscard(listingId) {
    // Only one undo toast at a time: an earlier pending delete goes through now.
    this.flushPendingDiscard();
    this.setState(prevState => ({
      pendingDiscard: listingId,
      hiddenListingIds: [...prevState.hiddenListingIds, listingId.uuid],
    }));
    this.discardTimeout = window.setTimeout(this.flushPendingDiscard, UNDO_DISCARD_WINDOW_MS);
  }

  undoDiscard() {
    window.clearTimeout(this.discardTimeout);
    const { pendingDiscard } = this.state;
    if (!pendingDiscard) {
      return;
    }
    this.setState(prevState => ({
      pendingDiscard: null,
      hiddenListingIds: prevState.hiddenListingIds.filter(id => id !== pendingDiscard.uuid),
    }));
  }

  flushPendingDiscard() {
    window.clearTimeout(this.discardTimeout);
    const { pendingDiscard } = this.state;
    if (pendingDiscard) {
      this.setState({ pendingDiscard: null });
      this.commitDiscard(pendingDiscard);
    }
  }

  commitDiscard(listingId) {
    // On success the draft leaves the store; on failure it reappears with an error.
    this.props.onDiscardDraft(listingId).then(() => {
      if (!this.unmounted) {
        this.setState(prevState => ({
          hiddenListingIds: prevState.hiddenListingIds.filter(id => id !== listingId.uuid),
        }));
      }
    });
  }

  render() {
    const {
      closingListing,
      closingListingError,
      discardingDraft,
      discardingDraftError,
      listings: allListings,
      onCloseListing,
      onOpenListing,
      openingListing,
      openingListingError,
      pagination,
      queryInProgress,
      queryListingsError,
      queryParams,
      scrollingDisabled,
      intl,
    } = this.props;
    const { pendingDiscard, hiddenListingIds } = this.state;
    const listings = allListings.filter(l => !hiddenListingIds.includes(l.id.uuid));

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const listingsAreLoaded = !queryInProgress && hasPaginationInfo;
    const listingCount = listings.length;
    const activeCount = listings.filter(l => l.attributes.state === LISTING_STATE_PUBLISHED).length;
    const closedCount = listings.filter(l => l.attributes.state === LISTING_STATE_CLOSED).length;
    const draftCount = listings.filter(l => l.attributes.state === LISTING_STATE_DRAFT).length;

    const loadingResults = (
      <h2>
        <FormattedMessage id="ManageProjectListingsPage.loadingOwnListings" />
      </h2>
    );

    const queryError = (
      <h2 className={css.error}>
        <FormattedMessage id="ManageProjectListingsPage.queryError" />
      </h2>
    );

    const noResults =
      listingsAreLoaded && listingCount === 0 ? (
        <h1 className={css.title}>
          <FormattedMessage id="ManageProjectListingsPage.noResults" />
        </h1>
      ) : null;

    const heading =
      listingsAreLoaded && listingCount > 0 ? (
        <div className={css.headingText}>
          <h1 className={css.title}>
            <FormattedMessage
              id="ManageProjectListingsPage.youHaveListings"
              values={{
                count: listingCount,
                b: chunks => <span className={css.headingCount}>{chunks}</span>,
              }}
            />
          </h1>
          <div className={css.statusSummary}>
            <span className={css.summaryItem}>
              <span className={classNames(css.dot, css.dotActive)} />
              <FormattedMessage
                id="ManageProjectListingsPage.activeCount"
                values={{ count: activeCount }}
              />
            </span>
            <span className={css.summaryItem}>
              <span className={classNames(css.dot, css.dotClosed)} />
              <FormattedMessage
                id="ManageProjectListingsPage.closedCount"
                values={{ count: closedCount }}
              />
            </span>
            <span className={css.summaryItem}>
              <span className={classNames(css.dot, css.dotDraft)} />
              <FormattedMessage
                id="ManageProjectListingsPage.draftCount"
                values={{ count: draftCount }}
              />
            </span>
          </div>
        </div>
      ) : (
        noResults
      );

    const page = queryParams ? queryParams.page : 1;
    const paginationLinks =
      listingsAreLoaded && pagination && pagination.totalPages > 1 ? (
        <PaginationLinks
          className={css.pagination}
          pageName="ManageProjectListingsPage"
          pageSearchParams={{ page }}
          pagination={pagination}
        />
      ) : null;

    const closingErrorListingId = !!closingListingError && closingListingError.listingId;
    const openingErrorListingId = !!openingListingError && openingListingError.listingId;
    const discardErrorListingId = !!discardingDraftError && discardingDraftError.listingId;

    const title = intl.formatMessage({ id: 'ManageProjectListingsPage.title' });

    return (
      <Page title={title} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn>
          <LayoutWrapperTopbar>
            <TopbarContainer currentPage="ManageProjectListingsPage" />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain>
            {queryInProgress ? loadingResults : null}
            {queryListingsError ? queryError : null}
            <div className={css.listingPanel}>
              <div className={css.headingContainer}>
                {heading}
                <div className={css.newListingButtonContainer}>
                  <NamedLink name="NewProjectListingPage" className={css.newListingButton}>
                    <IoIosAdd className={css.newListingButtonIcon} />
                    <FormattedMessage id="ManageListingCard.createProjectButton" />
                  </NamedLink>
                </div>
              </div>
              <div className={css.listingCards}>
                {listings.map(l =>
                  l.attributes.publicData.listingType === 'project' ? (
                    <ManageProjectListingCard
                      className={css.listingCard}
                      key={l.id.uuid}
                      listing={l}
                      actionsInProgressListingId={
                        openingListing || closingListing || discardingDraft
                      }
                      onCloseListing={onCloseListing}
                      onOpenListing={onOpenListing}
                      onDiscardDraft={this.startDiscard}
                      hasOpeningError={openingErrorListingId.uuid === l.id.uuid}
                      hasClosingError={closingErrorListingId.uuid === l.id.uuid}
                      hasDiscardError={discardErrorListingId.uuid === l.id.uuid}
                    />
                  ) : null
                )}
              </div>

              {paginationLinks}
            </div>
            {/* The live region stays mounted so screen readers announce the toast. */}
            <div role="status" aria-live="polite">
              {pendingDiscard ? (
                <div className={css.undoToast} key={pendingDiscard.uuid}>
                  <span>
                    <FormattedMessage id="ManageProjectListingsPage.draftDiscarded" />
                  </span>
                  <button type="button" className={css.undoButton} onClick={this.undoDiscard}>
                    <FormattedMessage id="ManageProjectListingsPage.undoDiscard" />
                  </button>
                  <span className={css.undoCountdown} aria-hidden="true" />
                </div>
              ) : null}
            </div>
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

ManageProjectListingsPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  queryListingsError: null,
  queryParams: null,
  closingListing: null,
  closingListingError: null,
  openingListing: null,
  openingListingError: null,
  discardingDraft: null,
  discardingDraftError: null,
};

const { arrayOf, bool, func, object, shape, string } = PropTypes;

ManageProjectListingsPageComponent.propTypes = {
  closingListing: shape({ uuid: string.isRequired }),
  closingListingError: shape({
    listingId: propTypes.uuid.isRequired,
    error: propTypes.error.isRequired,
  }),
  discardingDraft: shape({ uuid: string.isRequired }),
  discardingDraftError: shape({
    listingId: propTypes.uuid.isRequired,
    error: propTypes.error.isRequired,
  }),
  listings: arrayOf(propTypes.ownListing),
  onCloseListing: func.isRequired,
  onDiscardDraft: func.isRequired,
  onOpenListing: func.isRequired,
  openingListing: shape({ uuid: string.isRequired }),
  openingListingError: shape({
    listingId: propTypes.uuid.isRequired,
    error: propTypes.error.isRequired,
  }),
  pagination: propTypes.pagination,
  queryInProgress: bool.isRequired,
  queryListingsError: propTypes.error,
  queryParams: object,
  scrollingDisabled: bool.isRequired,
  onHideListing: func.isRequired,
  onShowListing: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    queryInProgress,
    queryListingsError,
    queryParams,
    openingListing,
    openingListingError,
    closingListing,
    closingListingError,
    discardingDraft,
    discardingDraftError,
  } = state.ManageProjectListingsPage;

  const listings = getOwnListingsById(state, currentPageResultIds);
  const { currentUser } = state.user;

  const nonProfileListings = listings.filter(
    listing => listing.attributes.publicData.listingType === 'project'
  );

  return {
    currentUser,
    currentPageResultIds,
    listings: nonProfileListings,
    pagination,
    queryInProgress,
    queryListingsError,
    queryParams,
    scrollingDisabled: isScrollingDisabled(state),
    openingListing,
    openingListingError,
    closingListing,
    closingListingError,
    discardingDraft,
    discardingDraftError,
  };
};

const mapDispatchToProps = dispatch => ({
  onCloseListing: listingId => dispatch(closeListing(listingId)),
  onOpenListing: listingId => dispatch(openListing(listingId)),
  onDiscardDraft: listingId => dispatch(discardDraft(listingId)),
  onHideListing: listingId => dispatch(hideListing(listingId)),
  onShowListing: listingId => dispatch(showListing(listingId)),
});

const ManageProjectListingsPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ManageProjectListingsPageComponent);

ManageProjectListingsPage.loadData = (params, search) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  return queryOwnListings({
    ...queryParams,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images'],
    'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    'limit.images': 1,
  });
};

export default ManageProjectListingsPage;
